import type { LegPointsPayload, LegRoutePoint, LegStatus } from '@/types/domain'

/* The chain's rules, as functions rather than as expressions inside a component.
 *
 * All three of these are claims about where a client's cargo is, and all three
 * are enforced on the server as well. They live here so the timeline that draws
 * the chain and the editor that rewrites it cannot arrive at two answers — the
 * same reason `overdue.ts` next door exists.
 */

/** How many points at the FRONT of the chain carry an arrival.
 *
 *  A prefix length rather than a count, and the difference is the point: the
 *  server refuses a chain whose reached marks leave a gap
 *  (`points.i.reached: not-contiguous`), so a chain arriving with one is a chain
 *  something has already gone wrong with. Counting matches instead of measuring
 *  the prefix would paper over it and hand the editor a number that submits a
 *  DIFFERENT chain than the one on screen. */
export function reachedCount(points: LegRoutePoint[]): number {
  let count = 0
  while (points[count]?.reachedAt !== undefined) count += 1
  return count
}

/** Whether the leg's own status says the cargo has left its origin.
 *
 *  The mirror of `LegStatus.HasDeparted` in `backend/internal/casefile/legs.go`,
 *  and it is repeated here for one specific refusal rather than for display: on
 *  a departed leg the server's `stampDeparture` re-marks the FIRST point of any
 *  chain that has none, so a client offering to clear it would offer an act that
 *  silently undoes itself. See `PointTimeline`.
 *
 *  Written as the four departed states rather than as `!== 'planned'` so a sixth
 *  status added later fails closed — an unknown state is not "already gone". */
export function hasDeparted(status: LegStatus): boolean {
  return (
    status === 'in-transit' ||
    status === 'waiting-border' ||
    status === 'arrived' ||
    status === 'completed'
  )
}

/** The whole chain, submitted with exactly the first `count` points reached.
 *
 *  The list goes WHOLE because that is the only write shape the chain has, and
 *  a prefix length is the only shape this helper accepts, because a caller that
 *  could pass a set of indices could pass a set with a gap in it. The server
 *  would refuse that, correctly, but a client should not be able to compose the
 *  refusal in the first place.
 *
 *  `reached: index < count` — AND NOTHING ELSE. It read
 *  `point.reachedAt !== undefined || index < count` until this was written,
 *  which is the line that made a mis-recorded arrival permanent: every already
 *  reached point re-submitted itself as reached, so no submission this client
 *  could build ever cleared one. The server has always supported it — see
 *  `Repo.ReplacePoints`, "newly cleared drops the pair, which is what makes a
 *  mis-ticked row correctable at all". Kept-true arrivals still keep their
 *  original instant: the preservation is the server's, keyed on position, and
 *  does not need the client to ask for it. */
export function chainWithReachedCount(
  points: LegRoutePoint[],
  count: number,
): LegPointsPayload {
  return {
    points: points.map((point, index) => ({
      name: point.name,
      country: point.country,
      plannedDaysFromPrevious: point.plannedDaysFromPrevious,
      reached: index < count,
    })),
  }
}
